import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import { SecondaryButton } from '../';
import {
  ExpandMore,
  Lightbulb,
  TrendingUp,
  Business,
  Build,
  Refresh,
  AutoAwesome,
  KeyboardArrowRight
} from '@mui/icons-material';
import { insightsService, Insight, InsightResponse } from '../../services/InsightsService';
import { useAuth } from '../../context/AuthContext';
import toastService from '../../utils/toastService';

interface InsightsCardProps {
  maxHeight?: number;
  showHeader?: boolean;
  empresaId?: string;
}

const InsightsCard: React.FC<InsightsCardProps> = ({ 
  maxHeight = 600, 
  showHeader = true,
  empresaId
}) => {
  const [insights, setInsights] = useState<InsightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedInsight, setExpandedInsight] = useState<string | false>(false);
  const { user } = useAuth();
  const theme = useTheme();

  // Usar empresaId passado por props ou tentar pegar do contexto
  const currentEmpresaId = empresaId || (user as any)?.empresa?.guid || '1';

  const carregarInsights = async () => {
    if (!currentEmpresaId) {
      setError('ID da empresa não encontrado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await insightsService.gerarInsights(currentEmpresaId);
      setInsights(response);
      toastService.success('Insights gerados com sucesso!');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao carregar insights';
      setError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Removido o useEffect que carregava automaticamente os insights
  // Agora os insights são gerados apenas quando o usuário clicar no botão

  const getPrioridadeColor = (prioridade: number) => {
    if (prioridade >= 4) return theme.palette.error.main;
    if (prioridade >= 3) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'OPORTUNIDADE': return theme.palette.success.main;
      case 'AMEACA': return theme.palette.error.main;
      case 'ALERTA': return theme.palette.warning.main;
      case 'MELHORIA': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'OPORTUNIDADE': return <TrendingUp />;
      case 'AMEACA': return <Business />;
      case 'ALERTA': return <Build />;
      case 'MELHORIA': return <AutoAwesome />;
      default: return <Lightbulb />;
    }
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedInsight(isExpanded ? panel : false);
  };

  return (
    <Card 
      elevation={2} 
      sx={{ 
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {showHeader && (
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight="medium">
                Gere Insights para sua empresa
              </Typography>
            </Box>
            <Tooltip title="Atualizar insights">
              <IconButton 
                onClick={carregarInsights}
                disabled={loading}
                size="small"
              >
                <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      )}

      <CardContent sx={{ pt: showHeader ? 0 : 2 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={carregarInsights}>
                Tentar novamente
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Gerando insights com IA...
            </Typography>
          </Box>
        )}

        {insights && !loading && (
          <Box>
            {/* Resumo Executivo */}
            {insights.resumoExecutivo && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                  📊 Resumo Executivo
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {insights.resumoExecutivo}
                </Typography>
              </Paper>
            )}

            {/* Lista de Insights */}
            <Box sx={{ maxHeight: maxHeight - 200, overflowY: 'auto' }}>
              {insights.insights.map((insight: Insight, index: number) => (
                <Fade in={true} timeout={300} key={`insight-${insight.titulo}-${index}`}>
                  <Accordion
                    expanded={expandedInsight === `panel-${index}`}
                    onChange={handleAccordionChange(`panel-${index}`)}
                    elevation={0}
                    sx={{
                      mb: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:before': { display: 'none' },
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        backgroundColor: alpha(getTipoColor(insight.tipo), 0.05),
                        '&:hover': {
                          backgroundColor: alpha(getTipoColor(insight.tipo), 0.1)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box sx={{ color: getTipoColor(insight.tipo) }}>
                          {getTipoIcon(insight.tipo)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {insight.titulo}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={insight.tipo} 
                              size="small" 
                              sx={{ 
                                backgroundColor: alpha(getTipoColor(insight.tipo), 0.2),
                                color: getTipoColor(insight.tipo),
                                fontSize: '0.7rem',
                                height: 20
                              }} 
                            />
                            <Chip 
                              label={`Prioridade ${insight.prioridade}`} 
                              size="small" 
                              sx={{ 
                                backgroundColor: alpha(getPrioridadeColor(insight.prioridade), 0.2),
                                color: getPrioridadeColor(insight.prioridade),
                                fontSize: '0.7rem',
                                height: 20
                              }} 
                            />
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Typography variant="body2">
                          {insight.descricao}
                        </Typography>

                        {insight.recomendacoes && insight.recomendacoes.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                              💡 Recomendações:
                            </Typography>
                            <Stack spacing={1}>
                              {insight.recomendacoes.map((rec: string, recIndex: number) => (
                                <Box 
                                  key={`rec-${index}-${recIndex}`} 
                                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                                >
                                  <KeyboardArrowRight sx={{ fontSize: 16, mt: 0.2, color: theme.palette.primary.main }} />
                                  <Typography variant="body2">
                                    {rec}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            📍 {insight.categoria} • {insight.fonte}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {Array.from({ length: insight.prioridade }, (_, i) => (
                              <Box 
                                key={i} 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: getPrioridadeColor(insight.prioridade) 
                                }} 
                              />
                            ))}
                          </Box>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Fade>
              ))}

              {insights.insights.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Lightbulb sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Nenhum insight foi gerado. Verifique se há dados suficientes na empresa.
                  </Typography>
                  <SecondaryButton 
                    variant="contained" 
                    onClick={carregarInsights}
                    sx={{ mt: 2 }}
                    startIcon={<Refresh />}
                  >
                    Gerar Insights
                  </SecondaryButton>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {!insights && !loading && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AutoAwesome sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Clique no botão para gerar insights estratégicos personalizados com IA
            </Typography>
            <SecondaryButton 
              variant="contained" 
              onClick={carregarInsights}
              startIcon={<AutoAwesome />}
              sx={{ mt: 2 }}
            >
              Ver Insights da IA
            </SecondaryButton>
          </Box>
        )}
      </CardContent>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Card>
  );
};

export default InsightsCard;